// Shared md-first ("Slide creator 2") deck composition. Builds the template +
// element catalogue payloads from the generated slide system and calls the
// admin AI composer. Used by both the slide-system sandbox and the unified
// Slide-Deck Creator intake (engine = "mdfirst"), so the two stay in lock-step.
import { slideSystem } from './slide-render.js';

/** The templates the composer may pick from: id, label, and its zone names. */
export function deckTemplatesPayload() {
  const zs = slideSystem.zonesets?.zonesets || {};
  return Object.entries(zs).map(([id, z]) => ({ id, label: z.label || id, zones: Object.keys(z.zones || {}) }));
}

/** The elements the composer may place: id, label, category, kind. */
export function deckElementsPayload() {
  const es = slideSystem.elementCatalog?.elements || {};
  return Object.entries(es).map(([id, e]) => ({ id, label: e.label || id, cat: e.cat, kind: e.kind }));
}

/** The standard slides catalogue: label, description, template, ground — so the
 *  AI knows which proven layouts exist before it reaches for raw templates. */
export function deckStandardsPayload() {
  const stds = slideSystem.standards;
  if (!Array.isArray(stds) || !stds.length) return [];
  return stds.map(s => ({ label: s.label, description: s.description || '', template: s.template, ground: s.ground || '' }));
}

/**
 * Compose a deck from a brief via /api/slide-system/compose-deck.
 * Returns { title, slides: [{ template, ground?, content:{zones} }, …] }.
 */
export async function composeDeckV2({ brief, count = 10, imageMode, imageStyle, variant, autoCount, capabilities, research }) {
  const res = await fetch('/api/slide-system/compose-deck', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    // autoCount lets the analytical grounding recommend a slide count that fits a
    // multi-ask brief; capabilities overrides the built-in eOS/eve grounding;
    // research:false skips web grounding. All optional + backward-compatible.
    body: JSON.stringify({ brief, count, imageMode, imageStyle, variant, autoCount, capabilities, research, templates: deckTemplatesPayload(), elements: deckElementsPayload(), standards: deckStandardsPayload() }),
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.error || `compose-deck → ${res.status}`);
  return j;
}

// localStorage key prefix for handed-off V2 decks — MUST match DeckView's
// V2_DECK_PREFIX so the viewer can resolve #…/<v2-id>/deck.
export const V2_DECK_PREFIX = 'slidecreator.v2deck.';

/** Store a composed deck and return its viewer id (prefixed `v2-` so the
 *  router opens DeckView in mode="v2"). */
export function stashV2Deck(deck) {
  const id = `v2-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
  try { localStorage.setItem(V2_DECK_PREFIX + id, JSON.stringify(deck)); } catch { /* quota — ignore */ }
  return id;
}

/** A short display title derived from the brief's first line. */
export function briefTitle(brief) {
  const line = String(brief || '').split('\n').map(s => s.trim()).find(Boolean) || 'Slide deck';
  const trimmed = line.length > 64 ? line.slice(0, 64).replace(/\s+\S*$/, '') + '…' : line;
  return trimmed;
}

/** Stash a still-"composing" deck (params only) and return its viewer id. The
 *  viewer runs the actual composition so the user sees the process. */
export function stashV2Pending(params) {
  const id = `v2-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
  const entry = { status: 'composing', slides: [], ...params };
  try { localStorage.setItem(V2_DECK_PREFIX + id, JSON.stringify(entry)); } catch { /* quota — ignore */ }
  return id;
}
